import classNames from "classnames";
import * as React from "react";

import { type FileRecord } from "@/shared/domain/files";

import styles from "./ModelFilePicker.module.css";
import { modelReadiness } from "./modelReadiness";

export interface ModelFileRowProps {
  record: FileRecord;
  /**
   * Where the file is, as the list wants to say it: a dataset and a path for
   * the org-wide list, a path and where it was inherited from for a device's
   * configurations.
   */
  location: React.ReactNode;
  /** Whether this row's artifact is being looked up. */
  resolving: boolean;
  /** Whether any row's artifact is being looked up, which makes every row unpickable. */
  busy: boolean;
  onPick: (record: FileRecord) => void;
  onIngest: (record: FileRecord) => void;
  /** Whether an unconverted row may be picked to start a conversion. */
  canIngest: boolean;
}

/**
 * One ingestable source, and everything needed to choose between it and a
 * same-named one elsewhere.
 *
 * A file with no current artifact stays on the list, saying so — rather than
 * being filtered out. A user who knows `broken.urdf` is there and cannot find
 * it here has been told the picker is broken; one who finds it reading
 * "Conversion failed" has been told something true, and is offered the
 * conversion that fixes it.
 *
 * Only a run already in flight is unpickable: nothing the user can press makes
 * a running worker finish sooner, and there is no artifact yet to load.
 */
export function ModelFileRow({
  record,
  location,
  resolving,
  busy,
  onPick,
  onIngest,
  canIngest,
}: ModelFileRowProps) {
  const { pick, reason } = modelReadiness(record, canIngest);
  const status = resolving ? "Loading model…" : reason;

  return (
    <li>
      <button
        type="button"
        className={classNames(styles.row, {
          [styles.rowUnavailable]: pick === null,
        })}
        disabled={pick === null || busy}
        onClick={() => (pick === "ingest" ? onIngest(record) : onPick(record))}
      >
        <span className={styles.rowText}>
          <span className={styles.rowName}>{record.name}</span>
          <span className={styles.rowLocation}>{location}</span>
        </span>
        {status !== undefined && (
          <span className={styles.rowStatus}>{status}</span>
        )}
      </button>
    </li>
  );
}
